import { ComponentType, ReactElement } from 'react'

import { Checkbox, Tag } from 'antd'
import { type CheckboxChangeEvent } from 'antd/es/checkbox'
import { useTranslation } from 'react-i18next'

import { ConditionalWrapper } from 'components/ConditionalWrapper'
import { Flex, Icon } from 'components/core'
import { TooltipComponentType } from 'configuration/fieldCreators/localTypes'
import { FieldTooltipProps } from 'features/MetricsGlossary/components/FieldTooltip'
import GroupByName from 'features/MetricsGlossary/components/GroupByName'
import MetricName from 'features/MetricsGlossary/components/MetricName'
import { DataSource } from 'features/MetricsGlossary/types'
import { isMetricField } from 'helpers/tables'
import { Field } from 'types'

import * as styles from './styles.scss'

interface Props<T> {
    column: Field<T>
    displayStateDisabled: boolean
    disabledTooltip?: TooltipComponentType<FieldTooltipProps>
    onCheckboxChange: (event: CheckboxChangeEvent, column: Field<T>) => void
    isDraggable?: boolean
    dataSource?: DataSource
}

function Setting<T>({
    column,
    displayStateDisabled,
    onCheckboxChange,
    disabledTooltip,
    isDraggable = true,
    dataSource,
}: Props<T>): ReactElement {
    const { t } = useTranslation('common')
    const TooltipType = disabledTooltip as ComponentType<FieldTooltipProps>
    return (
        <Flex justifyContent="space-between" className={styles['setting-row']}>
            <div>
                <ConditionalWrapper
                    condition={displayStateDisabled && !!disabledTooltip}
                    wrapper={(children) => (
                        <TooltipType
                            title={column.name ?? column.id}
                            showSubtext={false}
                        >
                            {children}
                        </TooltipType>
                    )}
                >
                    <Checkbox
                        checked={Boolean(column.isVisible)}
                        onChange={(event) => onCheckboxChange(event, column)}
                        disabled={displayStateDisabled}
                    >
                        {isMetricField(column) ? (
                            <MetricName
                                field={column}
                                metric={column.metric}
                                dataSource={dataSource}
                                addTooltip={
                                    !displayStateDisabled || !disabledTooltip
                                }
                                tooltipProps={{
                                    showSubtext: false,
                                }}
                            />
                        ) : (
                            <GroupByName
                                field={column}
                                groupBy={column.groupBy}
                                addTooltip={
                                    !displayStateDisabled || !disabledTooltip
                                }
                            />
                        )}
                    </Checkbox>
                </ConditionalWrapper>
            </div>
            <Flex>
                {isMetricField(column) && (
                    <Tag bordered={false} className={styles['metric-tag']}>
                        {t('common:metric', 'Metric')}
                    </Tag>
                )}
                {isDraggable && (
                    <Icon
                        name="DOTS_GRID_VERTICAL"
                        color="grayBackgroundColor"
                        size={13}
                    />
                )}
            </Flex>
        </Flex>
    )
}

export default Setting
